import { extractSectionId } from '../utils/extractSectionId.js';
import { afterAnimationEnd, formatMoney } from '../utils/utils.js';
import { ProductPreview } from './product-preview.js';

class CustomizationPopup extends HTMLElement {
    constructor() {
        super();
        this.variants = window.meta.product.variants || [];
        this.pageIndex = 1;
        this.useInsurance = this.hasAttribute('data-has-insurance');
    }

    connectedCallback() {
        /* this.setPage(1); */
        this.addBuyButtonBlockers();

        this.mainProductForm = document.querySelector('.shopify-product-form');

        if (
            window.themeVariables.settings.cartType !== 'page' ||
            window.themeVariables.settings.pageType !== 'cart'
        ) {
            this.cartDrawer = document.getElementById('cart-drawer');
        }

        this.popup = this.querySelector('.popup');
        this.popup.setAttribute('tabindex', '-1');

        this.setInsuranceVariables();
        
        // Initialize product preview
        this.productPreview = new ProductPreview(this);

        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('change', this.handleChange.bind(this));

        this.handleChange();
        this.addShopifyListeners();
    }

    addBuyButtonBlockers() {
        this.mainBuyButtons = document.querySelectorAll('buy-buttons');

        this.mainBuyButtons.forEach((btnContainer) => {
            btnContainer.classList.add('relative');

            const overlay = document.createElement('div');
            overlay.classList.add('absolute', 'top-0', 'left-0', 'w-full', 'h-full', 'pointer-events-none');

            btnContainer.appendChild(overlay);

            btnContainer.addEventListener('click', this.handleMainATCCLick.bind(this), true);
        });
    }

    handleMainATCCLick(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.open();
    }

    handleClick(event) {
        const target = event.target;
        console.log('[Insurance Debug] Starting handleClick function', target);

        /*   if (target.closest('[data-prev]')) {
            this.setPage(--this.pageIndex);
        } else if (target.closest('[data-next]')) {
            this.setPage(++this.pageIndex);
        } */

        if (target.closest('[data-close]')) {
            this.close();
        }
        if (target.closest('[data-submit]')) {
            this.submit();
        }
        if (target.closest('[data-preview-product]')) {
            event.preventDefault();
            event.stopPropagation();
            const button = target.closest('[data-preview-product]');
            this.productPreview.show(button.dataset.previewProduct, button.dataset.variantId);
        }
    }

    setPage(index) {
        if (!this.pages) {
            this.pages = this.querySelectorAll('[data-page]');
        }

        this.pages.forEach((page) => page.classList.toggle('hidden', page.dataset.page != index));
    }

    handleChange() {
        this.selectedUpsellInputs = Array.from(this.querySelectorAll('input[name="upsell"]:checked')) || [];
        this.deliveryMethod = this.querySelector('input[name="delivery"]:checked')?.value;

        if (this.useInsurance) {
            try {
                this.checkAvailableInsurance();
            } catch (error) {
                console.error(error);
            }
        }

        this.updatePrice();
        this.updateDeliveryUI();
    }

    close() {
        this.classList.add('is-closing');
        this.isOpen = false;

        afterAnimationEnd(this.popup, () => {
            this.classList.add('hidden');
            this.classList.remove('is-closing');
        });

        CustomizationPopup.lockBody(false);
    }

    open() {
        this.popup.focus();

        this.isOpen = true;
        this.classList.remove('hidden');
        this.classList.add('is-opening');
        afterAnimationEnd(this.popup, () => {
            this.classList.remove('is-opening');
        });

        CustomizationPopup.moveWhatsAppIcon(true);
        CustomizationPopup.lockBody(true);
    }

    updatePrice() {
        if (!this.priceEl) {
            this.priceEl = this.querySelector('[data-total-price]');
            if (!this.priceEl) return console.warn("Can't find price el in customization form", this);
        }

        let price = this.currentMainVariant.price + this.totalUpsellPrice;

        if (this.deliveryMethod == 'assembled') {
            price += window.themeVariables.settings.assembledCost;
        }

        const formattedPrice = formatMoney(price);

        this.priceEl.textContent = formattedPrice;
    }

    updateDeliveryUI() {
        const delivery = this.deliveryMethod;
        const showDeliveryEls = this.querySelectorAll('[data-show-delivery]');
        showDeliveryEls.forEach((el) => el.classList.toggle('hidden', el.dataset.showDelivery !== delivery));
    }

    get currentMainVariant() {
        const currentVariantId = this.mainProdutFormDataObj.id;
        return this.variants.find((variant) => variant.id == currentVariantId);
    }

    get totalUpsellPrice() {
        return this.selectedUpsellInputs.reduce((acc, input) => {
            try {
                const priceString = input.dataset.price;
                const priceInt = parseInt(priceString);

                return acc + priceInt;
            } catch (error) {
                console.error(error);
                return acc;
            }
        }, 0);
    }

    get selectedUpsellCartItems() {
        return this.selectedUpsellInputs.map((input) => {
            return {
                id: input.value,
                quantity: 1
            };
        });
    }

    get mainProdutFormDataObj() {
        const formData = {};
        const inputs = this.mainProductForm.querySelectorAll('input, select, textarea');

        inputs.forEach((input) => {
            if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) {
                return;
            }
            if (input.name) {
                formData[input.name] = input.value;
            }
        });

        return formData;
    }

    checkAvailableInsurance() {
        let allowInsurance = true;

        const hasAllRequiredProducts = this.hasAllRequiredProductsForInsurance();

        //check for delivery method
        if (this.deliveryMethod) {
            allowInsurance = this.deliveryMethod === 'assembled' && hasAllRequiredProducts;
        } else {
            allowInsurance = hasAllRequiredProducts;
        }

        if (!allowInsurance) {
            this.insuranceInput.checked = false;
        }

        this.requiredInsuranceContainer.classList.toggle('hidden', allowInsurance);
        this.insuranceContainer.toggleAttribute('disabled', !allowInsurance);

        return allowInsurance;
    }

    hasAllRequiredProductsForInsurance() {
        /*
            So basically, we don't actually want to use the "required for insurance" input unless 
            it's the only place where the product shows up. If the product is somewhere else, we 
            want to check that input and hide the input in "required for insurance".
        */
        const checkOtherInput = (inputEl) => {
            let hasOtherInput = false;

            const allInputsWithValue = this.querySelectorAll(
                `input[name="upsell"][value="${inputEl.value}"]`
            );

            if (allInputsWithValue.length > 1) {
                hasOtherInput = true;
                if (inputEl.checked) {
                    allInputsWithValue.forEach((el) => {
                        el.checked = el !== inputEl;
                    });
                }
            }

            return hasOtherInput;
        };

        const checkSelected = (inputEl) => {
            const id = inputEl.value;

            const hasOtherInput = checkOtherInput(inputEl);

            const selected = this.selectedUpsellCartItems.some((item) => item.id == id);

            //If it's selected and there's another input, hide it
            const hideInput = selected && hasOtherInput;
            const listEl = inputEl.closest('li');
            listEl.classList.toggle('hidden', hideInput);

            return selected;
        };

        //Should have all items in requiredInsuranceUpsellInputEls
        const hasAllInsuranceUpsellEls = this.requiredInsuranceUpsellInputEls.every(checkSelected);

        //Should have at least one of each item in requiredInsuranceCollections
        const hasRequiredCollectionItems = this.requiredInsuranceCollections.every((variantIdArray) => {
            const cartItems = [...this.selectedUpsellCartItems];
            const selectedId = variantIdArray.find((id) => cartItems.some((item) => item.id == id));

            const hasSomeSelected = selectedId != null;

            //If there's a input in upsells, check the other one
            const selectedInputEl = this.querySelector(
                `[data-insurance-upsells] input[value="${selectedId}"]`
            );
            if (selectedInputEl) {
                checkSelected(selectedInputEl);
            }

            //Hide the insurance items from colection in insurance upsells
            variantIdArray.forEach((id) => {
                const listEl = this.querySelector(`[data-insurance-upsells] li:has(input[value="${id}"])`);
                if (listEl) {
                    listEl.classList.toggle('hidden', hasSomeSelected);
                }
            });

            return hasSomeSelected;
        });

        const hasAllRequiredProducts = hasAllInsuranceUpsellEls && hasRequiredCollectionItems;

        return hasAllRequiredProducts;
    }

    setInsuranceVariables() {
        this.insuranceContainer = this.querySelector('[data-insurance]');
        if (!this.insuranceContainer) return console.error('Unable to find insurance container');

        this.insuranceInput = this.insuranceContainer.querySelector('input[type="checkbox"]');
        this.requiredForInsuranceIds = this.insuranceContainer.dataset.insuranceRequired?.split(',') || [];

        this.requiredInsuranceCollectionsRaw = this.insuranceContainer.dataset.insuranceRequiredCollections;
        this.requiredInsuranceCollections = JSON.parse(atob(this.requiredInsuranceCollectionsRaw));

        this.requiredInsuranceContainer = this.querySelector('[data-insurance-upsells]');

        this.requiredInsuranceUpsellInputEls = Array.from(
            this.querySelectorAll('[data-insurance-upsells] input[name="upsell"]')
        );
        //copy requiredInsuranceCollections to data-insurance-upsells
        this.copyInsuranceCollectionsUpsells();
    }

    copyInsuranceCollectionsUpsells() {
        const insuranceList = this.querySelector('[data-insurance-upsells] ul');

        this.requiredInsuranceCollections.forEach((variantIdArr) => {
            variantIdArr.some((id) => {
                const el = this.querySelector(`li:has(input[value="${id}"])`);
                if (el) {
                    const clonedEl = el.cloneNode(true); // Deep clone
                    insuranceList.append(clonedEl);
                    return true;
                }
                return false;
            });
        });
    }

    submit() {
        console.log('[Insurance Debug] Starting submit function');
        const productItemObj = this.mainProdutFormDataObj;
        if (this.deliveryMethod) {
            productItemObj.properties = productItemObj.properties || {};
            productItemObj.properties._delivery = this.deliveryMethod;

            const deliveryString = window.themeVariables.strings.delivery;
            const deliveryMethodString = window.themeVariables.strings[this.deliveryMethod];

            productItemObj.properties[deliveryString] = deliveryMethodString;
        }

        //ensure quantity
        productItemObj.quantity = productItemObj.quantity || 1;

        const items = [productItemObj, ...this.selectedUpsellCartItems];

        this.setRequiredInsuranceIds(items);

        this.addToCart(items);
    }

    setRequiredInsuranceIds(items) {
        const productItemObj = this.mainProdutFormDataObj;
        console.log('[Insurance Debug] Starting setRequiredInsuranceIds with items:', items);
        console.log('[Insurance Debug] Main product object:', productItemObj);

        if (this.insuranceInput && this.insuranceInput.checked) {
            const insuranceItemId = this.insuranceInput.value;
            console.log('[Insurance Debug] Insurance input checked. Insurance item ID:', insuranceItemId);

            const insuranceItemIndex = items.findIndex(
                (item, index) => index > 0 && item.id == insuranceItemId
            );
            console.log('[Insurance Debug] Found insurance item at index:', insuranceItemIndex);

            if (insuranceItemIndex > -1) {
                const requiredIds = [
                    productItemObj.id,
                    ...this.requiredInsuranceUpsellInputEls.map((input) => input.value)
                ];
                console.log('[Insurance Debug] Required IDs collected:', requiredIds);

                if (requiredIds.length > 0) {
                    const requiredIdsString = JSON.stringify(requiredIds);
                    const base64RequiredIds = btoa(requiredIdsString);
                    console.log('[Insurance Debug] Base64 encoded required IDs:', base64RequiredIds);

                    items[insuranceItemIndex].properties = items[insuranceItemIndex].properties || {};
                    items[insuranceItemIndex].properties._req = base64RequiredIds;
                    console.log(
                        '[Insurance Debug] Updated insurance item properties:',
                        items[insuranceItemIndex].properties
                    );
                }
            }
        } else {
            console.log('[Insurance Debug] Insurance input not checked or not found');
        }
    }

    addShopifyListeners() {
        document.addEventListener('shopify:block:select', this.openModalOnSelect);
        document.addEventListener('shopify:section:select', this.openModalOnSelect);
        document.addEventListener('shopify:section:deselect', this.closeModalOnDeselect);

        document.addEventListener('variant:change', this.addBuyButtonBlockers.bind(this));
        document.addEventListener('product:rerender', this.addBuyButtonBlockers.bind(this));

        window.addEventListener('popstate', () => {
            // Re-add button blockers when URL/history changes
            this.addBuyButtonBlockers();
        });
    }

    openModalOnSelect(e) {
        const selectedSection =
            e.target.querySelector('.shopify-section') || e.target.closest('.shopify-section') || e.target;

        if (selectedSection === this.section) {
            this.open();
        }
    }

    closeModalOnDeselect() {
        if (this.isOpen) {
            this.close();
        }
    }

    setError(string) {
        if (!this.errorEl) {
            this.errorEl = this.querySelector('[data-error]');
            if (!this.errorEl) return console.error("Can't find error element");
        }

        if (string) {
            this.errorEl.classList.remove('hidden');
            this.errorEl.textContent = string;
        } else {
            this.errorEl.classList.add('hidden');
            this.errorEl.textContent = '';
        }
    }

    getSectionIds() {
        if (this.cartDrawer) {
            const sectionId = extractSectionId(this.cartDrawer);
            return [sectionId];
        }
        return [];
    }

    async fetchCartContent() {
        const url = new URL(`${window.location.origin}${window.themeVariables.routes.cart}`);

        const cartContent = await fetch(url, {
            headers: {
                Accept: 'application/javascript',
                'Content-Type': 'application/json'
            }
        }).then((res) => res.json());

        return cartContent;
    }

    async addToCart(items) {
        this.setError();

        const body = { items };

        body.sections = this.getSectionIds();

        return fetch(window.themeVariables.routes.cartAdd, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                Accept: 'application/javascript',
                'Content-Type': 'application/json'
            }
        })
            .then(async (res) => {
                if (res.ok) {
                    const responseJson = await res.json();

                    this.close();

                    if (!this.cartDrawer) {
                        return (window.location.href = `${Shopify.routes.root}cart`);
                    }

                    const cartContent = await this.fetchCartContent();

                    cartContent['sections'] = responseJson['sections'];

                    this.dispatchEvent(
                        new CustomEvent('variant:add', {
                            bubbles: true,
                            detail: {
                                items: responseJson.hasOwnProperty('items')
                                    ? responseJson['items']
                                    : [responseJson],
                                cart: cartContent
                            }
                        })
                    );
                    document.documentElement.dispatchEvent(
                        new CustomEvent('cart:change', {
                            bubbles: true,
                            detail: {
                                baseEvent: 'variant:add',
                                cart: cartContent
                            }
                        })
                    );
                } else {
                    const data = await res.json();
                    console.error('Failed to add items to cart', data);
                    if (data.message) {
                        this.setError(data.message);
                    }
                }
            })
            .catch((error) => {
                // Handle network error
                console.error('Network error:', error);
            });
    }


    /* Static Methods */

    static moveWhatsAppIcon(bool) {
        const whatsAppBtn = document.querySelector('.gty-wacb-wrapper');
        if (!whatsAppBtn) return;
        whatsAppBtn.classList.toggle('hidden', bool);
    }

    static lockBody(bool) {
        if (bool) {
            // Prevents page behind from scrolling
            this.scrollY = window.scrollY;
            document.documentElement.classList.add('lock');
            /* document.body.classList.add('fixed', 'overflow-hidden'); */
            document.body.style.top = `-${this.scrollY}px`;
            document.documentElement.style.height = '100vh';
        } else {
            // Restore page position and scroll behaviour.
            document.documentElement.style.height = '';
            document.body.style.top = '';
            document.documentElement.classList.remove('lock');
            window.scrollTo(0, this.scrollY);
        }
    }
}

customElements.define('customization-popup', CustomizationPopup);
